import type { V10DomainEventName } from './outbox-event.js';
import type { DomainEventType } from './envelope.js';

/**
 * Compat mapping: v10 PascalCase event names ↔ pre-v10 dot-case names that
 * are still flowing through the legacy `core/event/publisher.ts` path.
 *
 * Why this exists:
 *   - Anayasa §17 says "Eski dot-case eventleri varsa migration/compat
 *     mapping açıkça dokümante edilmeli." We document the mapping here in
 *     code so the dispatcher / consumer / projection sides have one
 *     authoritative place to look.
 *   - Phase 1 only LANDS the schema and the dispatcher skeleton. Wiring
 *     the existing dot-case publish points onto the outbox path is a
 *     follow-up — the gap is tracked under
 *     `PARTIAL_OUTBOX_SCHEMA_READY_DISPATCHER_PENDING`.
 *
 * `null` on the legacy side means "no pre-v10 equivalent existed; this is a
 * brand-new v10 event introduced by Phase 1". `null` on the v10 side means
 * "legacy event has no v10 mapping yet — it is not in the v10 registry".
 */
export const V10_TO_LEGACY_EVENT_NAME: Readonly<
  Record<V10DomainEventName, DomainEventType | null>
> = {
  OrderCreated: 'order.created',
  OrderCompleted: 'order.delivery_completed',
  OrderCancelled: 'order.cancelled',
  ReservationRequested: null,
  ReservationGranted: 'reservation.granted',
  ReservationRejected: null,
  BalanceDebitRequested: null,
  BalanceDebited: null,
  LedgerEntryCreated: 'billing.ledger_entry_posted',
  FulfillmentRequested: null,
  FulfillmentStarted: null,
  FulfillmentCompleted: null,
  FulfillmentFailed: 'order.delivery_failed',
  MailcomParentServed: null,
  MailcomParentRenewalRequested: null,
  MailcomParentRenewalCompleted: null,
  MailcomSessionRefreshRequested: null,
  MailcomSessionRefreshed: null,
  MailMessagePollingRequested: null,
  MailMessageReceived: null,
  // Phase 15 — V2-native provider control plane events (no legacy dot-case equivalent)
  ProviderAccountCreated: null,
  ProviderAliasImported: null,
  ProviderAliasStatusChanged: null,
  ProviderInventoryRecomputed: null,
  ProviderRenewalRequested: null,
  // Phase 17 — renewal job lifecycle status changes
  ProviderRenewalStatusChanged: null,
  // Phase 35C — credential_ref rotated on an existing provider_accounts row
  // via the admin UI (POST /admin/providers/mailcom/accounts/:id/credentials).
  // Payload carries credentialRefScheme only; raw credential / blob id never
  // appear in the outbox payload.
  ProviderAccountCredentialRefUpdated: null,
  // Track-2 (2026-05-10) — terminal failure cascade events emitted by the
  // worker-inbox-polling service on terminal upstream auth failure
  // (invalid_grant, account_locked, fail-closed unknown response).
  ProviderAccountDisabled: null,
  OrderCancellationRequested: null,
  // Balance tier system (2026-06-20) — brand-new v10 event, no legacy dot-case
  // equivalent. Emitted atomically with the wallet credit + ledger row when a
  // top-up settles; feeds the user_topup_tier_metrics projection.
  BalanceTopupSettled: null,
  // Admin↔member support chat (2026-06-21, D-231) — brand-new v10 events, no
  // legacy dot-case equivalent. Emitted atomically with the chat OLTP write;
  // feed the projection-chat read-models. Reuse the order.events topic
  // (partitionKey=conversationId preserves per-thread ordering).
  ChatMessageSent: null,
  ChatThreadRead: null,
};

export const LEGACY_TO_V10_EVENT_NAME: Readonly<
  Partial<Record<DomainEventType, V10DomainEventName>>
> = (() => {
  const out: Partial<Record<DomainEventType, V10DomainEventName>> = {};
  for (const [v10, legacy] of Object.entries(V10_TO_LEGACY_EVENT_NAME) as ReadonlyArray<
    [V10DomainEventName, DomainEventType | null]
  >) {
    if (legacy !== null) out[legacy] = v10;
  }
  return out;
})();

export const V10_DOMAIN_EVENT_NAMES: readonly V10DomainEventName[] = Object.keys(
  V10_TO_LEGACY_EVENT_NAME,
) as readonly V10DomainEventName[];

export function isV10DomainEventName(value: string): value is V10DomainEventName {
  return (V10_TO_LEGACY_EVENT_NAME as Record<string, unknown>)[value] !== undefined;
}
